import type { AudioFramePayload } from "./aiHub/types";
import { rhythmMusicActive } from "./aiHub/rhythmAudio";
import {
  deriveRhythmAmplitudeFromFrequencyData,
  isRhythmInputActive,
  rawFrequencyPeak,
  type RhythmActivityInput,
} from "./deriveRhythmAmplitude";

/** 仅用于「长时间无有效帧」过期清理（不在单帧判定里使用） */
export const RHYTHM_FRAME_STALE_MS = 250;

/** 明显底噪：峰值与振幅都极低 → 停止播放 */
export const RHYTHM_NOISE_PEAK = 0.018;
export const RHYTHM_NOISE_AMP = 0.022;

let lastLiveFrameAt = 0;

export function noteRhythmLiveFrame(at = Date.now()): void {
  lastLiveFrameAt = at;
}

export function resetRhythmPlaybackGate(): void {
  lastLiveFrameAt = 0;
}

export function getRhythmLastLiveFrameAt(): number {
  return lastLiveFrameAt;
}

/** 是否为底噪/静音（比播放判定更严，用于停止后全黑） */
export function isRhythmNoiseOnly(input: RhythmActivityInput): boolean {
  if (!input.frequencyData?.length) return true;

  const rawPeak = rawFrequencyPeak(input.frequencyData);
  const amp = Math.max(
    input.currentAmplitude ?? 0,
    input.rms ?? 0,
    deriveRhythmAmplitudeFromFrequencyData(input.frequencyData)
  );

  return rawPeak < RHYTHM_NOISE_PEAK && amp < RHYTHM_NOISE_AMP;
}

/**
 * 是否处于「正在播放音乐」状态。
 * 播放中放宽（避免误杀）；停止后靠 isRhythmNoiseOnly + 帧过期清理全黑。
 */
export function isRhythmPlaybackActive(
  input: RhythmActivityInput,
  now = Date.now()
): boolean {
  if (!input.frequencyData?.length) return false;
  if (isRhythmNoiseOnly(input)) return false;
  if (!isRhythmInputActive(input)) return false;

  noteRhythmLiveFrame(input.timestamp ?? now);
  return true;
}

/** 整合后的 AudioFramePayload 是否仍应驱动律动 */
export function isRhythmFramePlaybackActive(
  frame: AudioFramePayload,
  frequencyData?: number[] | null
): boolean {
  if (!rhythmMusicActive(frame)) return false;

  if (frequencyData?.length) {
    return isRhythmPlaybackActive({
      frequencyData,
      currentAmplitude: frame.rms,
      rms: frame.rms,
      rhythmSpectrum: frame.spectrum,
      timestamp: frame.timestamp,
    });
  }

  return frame.rms >= 0.02;
}
